import React from 'react'
import {
    BlockWrapper,
    defineBlock,
    smartProps,
    useEditor,
    useInspectorControl,
    useSmartProp,
} from '@smartfire/js-block-engine'
import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { TeamScore } from './TeamScores'
import type { EquipeScore } from '../competitions/types'
import { useStatsPerformMatch } from '../contexts/StatsPerformMatchesContext'
import style from './block.module.scss'
import { formatToDayJsDate } from '../utils/statsperform/statsperformUtils'
import { MatchStatus } from '../utils/statsperform/types'
import { LiveMatchStatus } from '../components/LiveMatchStatus'
import { flagUrl } from '../shared/utils'
import { Tv } from '../types'
import { useStatsPerformContestantsData } from '../hooks/useStatsPerformContestantsData'

type Team = {
    name: string
    flag?: { url?: string } | null
    score: number | EquipeScore
    halfTimeScore?: number | null
    extStructureId?: number
}

type ScoreProps = {
    home: Team
    away: Team
    date: string | null | Dayjs
    title?: string
    tvs?: Tv[]
    status?: MatchStatus
    size?: 'medium' | 'large'
    template?: 'home' | 'event'
    category?: string
    link?: string
    error?: boolean
    updating?: boolean
}

type BlockProps = ScoreProps & {
    //livePostId?: string
    statPerformMatchId?: string
    livePostLink?: string
    livePostId?: number
}

const TeamPreviewData: Team = {
    name: 'PREVIEW LIVE',
    flag: null,
    score: 0,
}

const InspectorLazy = React.lazy(() => import('./Inspector'))

export const ScoreDisplay: React.FC<ScoreProps> = ({
    home,
    away,
    date,
    title,
    tvs,
    status,
    size = 'medium',
    template = 'event',
    category,
    link,
    error,
    updating,
}) => {
    const { isEditing } = useEditor()
    const isHomeTemplate = template === 'home'
    const isTileLink = Boolean(isHomeTemplate && link) && !isEditing
    const isTvItemLink = !isEditing

    const Tag = isTileLink ? 'a' : 'div'
    const TvItemTag = isTvItemLink ? 'a' : 'div'

    const showCategory = Boolean(isHomeTemplate && category)

    const isMatchFinished = dayjs(date).endOf('day').isBefore(dayjs())

    return (
        <Tag
            href={isTileLink ? link : undefined}
            className={clsx(
                style.component,
                style[template],
                isTileLink && style.link,
                isMatchFinished && style.matchFinished,
            )}
        >
            {error ? (
                <div
                    className={style.error}
                    title="Erreur de synchro, veuilliez rafraîchir la page."
                >
                    ⚠️
                </div>
            ) : null}

            {showCategory ? <p className={style.category}>{category}</p> : null}

            <div className={clsx(style.header, style[size])}>
                <p className={style.live}>
                    <LiveMatchStatus
                        status={status ?? 'Fixture'}
                        date={date}
                        defaultDisplay={null}
                    />
                </p>

                {title ? <p className={style.title}>{title}</p> : null}

                {date ? (
                    <p className={style.date}>{dayjs(date).format('dddd D MMMM YYYY à HH[H]mm')}</p>
                ) : null}
            </div>

            <TeamScore
                homeScore={home.score}
                awayScore={away.score}
                homeName={home.name}
                awayName={away.name}
                homeLogoUrl={home.flag ? home.flag.url : null}
                awayLogoUrl={away.flag ? away.flag.url : null}
                homeExtStructureId={home.extStructureId}
                awayExtStructureId={away.extStructureId}
                size={size}
                template={template}
                updating={updating}
            />

            {home?.halfTimeScore && away?.halfTimeScore ? (
                <div className={style.halfTimeBlock}>
                    <span className={clsx(style.halfTimeScore, updating && style.updating)}>
                        {home.halfTimeScore ?? '- '}-{away.halfTimeScore ?? ' -'}
                    </span>
                    <span className={clsx(style.halfTimeText, style[size])}>MI-TEMPS</span>
                </div>
            ) : null}

            {/* todo: styles */}
            {tvs?.length ? (
                <div className={style.tvsBlock}>
                    <span className={style.label}>REGARDER LE MATCH</span>
                    <div className={style.tvs}>
                        {tvs
                            .filter((x) => Boolean(x.logo?.url))
                            .map((tv, i) => (
                                <TvItemTag
                                    key={i}
                                    title={tv?.name || undefined}
                                    {...(isTvItemLink && tv?.link !== ''
                                        ? {
                                              href: tv?.link,
                                              target: '_blank',
                                              rel: 'noopener noreferrer',
                                          }
                                        : {})}
                                >
                                    <img src={tv?.logo?.url} alt={tv?.name} />
                                </TvItemTag>
                            ))}
                    </div>
                </div>
            ) : null}
        </Tag>
    )
}

export const Score: React.FC<BlockProps> = ({
    home,
    away,
    date,
    title,
    tvs,
    status,
    size = 'medium',
    template = 'event',
    category,
    link,
    statPerformMatchId,
    livePostLink,
    livePostId,
}) => {
    useInspectorControl(InspectorLazy, [])

    const match = useStatsPerformMatch(statPerformMatchId)
    const { isEditing } = useEditor()
    const { data: matchDetails, error, updating } = match ?? {}
    const { homeContestant, awayContestant } = useStatsPerformContestantsData(statPerformMatchId)

    // If a post id is setup in admin but not statsPerformId is returned by block.php, there is an error
    if (!isEditing && livePostId && !statPerformMatchId) {
        return null
    }

    const isNotAdminPreview = !(isEditing && livePostId)

    type Team = {
        name: string
        flag?: { url?: string } | null
        score: number | EquipeScore
        halfTimeScore?: number | null
        extStructureId?: number
    }

    const homeData: Team = matchDetails
        ? {
              name: homeContestant?.name ?? '',
              flag: {
                  url: flagUrl(homeContestant?.logo ?? ''),
              },
              score: matchDetails.liveData?.matchDetails.scores?.final?.home ?? null,
              halfTimeScore: matchDetails.liveData?.matchDetails.scores?.ht?.home ?? null,
          }
        : isNotAdminPreview
        ? home
        : TeamPreviewData

    const awayData: Team = matchDetails
        ? {
              name: awayContestant?.name ?? '',
              flag: {
                  url: flagUrl(awayContestant?.logo ?? ''),
              },
              score: matchDetails.liveData?.matchDetails.scores?.final?.away ?? null,
              halfTimeScore: matchDetails.liveData?.matchDetails.scores?.ht?.away ?? null,
          }
        : isNotAdminPreview
        ? away
        : TeamPreviewData

    const dateData = matchDetails
        ? formatToDayJsDate(matchDetails.matchInfo)
        : isNotAdminPreview
        ? date
        : ''

    const statusData = matchDetails
        ? matchDetails.liveData?.matchDetails.matchStatus
        : isNotAdminPreview
        ? status
        : undefined

    const linkData = livePostLink ?? link

    const titleData = matchDetails
        ? `${matchDetails.matchInfo.competition.name} - ${matchDetails.matchInfo.round.name}`
        : isNotAdminPreview
        ? title
        : `Match Live ${livePostId}`

    return (
        <ScoreDisplay
            {...{
                home: homeData,
                away: awayData,
                date: dateData,
                title: titleData,
                tvs,
                size,
                template,
                category,
                link: linkData,
                error,
                updating,
                status: statusData,
            }}
        />
    )
}

const ScoreBlock: React.FC<BlockProps> = (props) => {
    const isHomeTemplate = props.template === 'home'
    const [livePostId] = useSmartProp<number | undefined>('livePostId') // We need update during admin
    return (
        <BlockWrapper.Div className={clsx(style.block, !isHomeTemplate && 'content-width')}>
            <Score {...props} livePostId={livePostId} />
        </BlockWrapper.Div>
    )
}

export default defineBlock({
    Block: ScoreBlock,
    smartProps: {
        home: smartProps.object.withDefault({
            name: 'France',
            flag: null,
            score: 40,
        }),
        away: smartProps.object.withDefault({
            name: 'Croatie',
            flag: null,
            score: 37,
        }),
        title: smartProps.string.withDefault(''),
        date: smartProps.string.withDefault(''),
        tvs: smartProps.array.withDefault([]),
        status: smartProps.string.withDefault('Fixture'),
        template: smartProps.string.withDefault('event'),
        category: smartProps.string.withDefault('Handball'),
        link: smartProps.string.withDefault(''),
        livePostId: smartProps.number.withDefault(0),
    },
    setup: {
        title: 'Score',
    },
})
